/**
 * ChaseTheAce Style1 - Premium Card Game Layout sections for GridEngine
 * Full-screen blue/indigo/purple gradient, animated floating cards, glowing orbs
 */
import React from 'react'
import Sparkles from 'lucide-react/dist/esm/icons/sparkles.js';
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import ArrowRight from 'lucide-react/dist/esm/icons/arrow-right.js';
import Ticket from 'lucide-react/dist/esm/icons/ticket.js';
import Clock from 'lucide-react/dist/esm/icons/clock.js';
import Users from 'lucide-react/dist/esm/icons/users.js';
import TrendingUp from 'lucide-react/dist/esm/icons/trending-up.js';
import Calendar from 'lucide-react/dist/esm/icons/calendar.js';
import InlineTranslationEdit from '../../../components/InlineTranslationEdit'
import HeroSiteLogo from '../../../components/HeroSiteLogo'
import CountdownTimerStyles from '../../../components/CountdownTimerStyles'
import DrawCountdownHeader from '../../../components/DrawCountdownHeader'
import DrawDateBadge from '../../../components/DrawDateBadge'
import HeroPartnerGlassSelect from '../../../components/HeroPartnerGlassSelect'
import { useContent } from '../../../contexts/ContentContext'
import { useLanguage } from '../../../contexts/LanguageContext'
import { useTranslations } from '../../../contexts/TranslationContext'
import { usePurchaseNavigate } from '../../../hooks/usePurchaseNavigate'
import { useCountdown } from '../../../hooks/useCountdown'
import { useCountdownStyle } from '../../../contexts/CountdownStyleContext'
import { useCharityTheme } from '../../../contexts/CharityThemeContext'
import { useSiteConfig } from '../../../contexts/SiteConfigContext'
import { charityConfig } from '../../../config/charity'
import { resolveCtaTargetCard, formatTargetCardName, formatCtaTargetCardText } from '../../../utils/ctaHelpers'
import { formatCurrencyValue } from '../../../utils/formatCurrency'

// Card suits with colors
const suits = [
  { symbol: '♠', name: 'Spades', color: 'text-gray-900 dark:text-white' },
  { symbol: '♥', name: 'Hearts', color: 'text-red-500' },
  { symbol: '♦', name: 'Diamonds', color: 'text-red-500' },
  { symbol: '♣', name: 'Clubs', color: 'text-gray-900 dark:text-white' },
]

const cardValues = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K']

const formatDynamicText = (template, replacements) => {
  return Object.entries(replacements).reduce(
    (text, [key, value]) => text.replaceAll(`{{${key}}}`, value),
    template || ''
  )
}

/**
 * PlayingCard component - renders a styled playing card
 */
export const PlayingCard = ({ value, suit, eliminated = false, isAce = false, size = 'md' }) => {
  const suitData = suits.find(s => s.symbol === suit) || suits[0]
  const sizeClasses = {
    sm: { card: 'w-8 h-11', cornerRank: 'text-[6px]', cornerSuit: 'text-[5px]', centerSuit: 'text-sm' },
    md: { card: 'w-12 h-16', cornerRank: 'text-[8px]', cornerSuit: 'text-[7px]', centerSuit: 'text-lg' },
    lg: { card: 'w-16 h-[5.5rem]', cornerRank: 'text-xs', cornerSuit: 'text-[10px]', centerSuit: 'text-xl' },
    xl: { card: 'w-24 h-32', cornerRank: 'text-sm', cornerSuit: 'text-xs', centerSuit: 'text-3xl' },
  }
  const sizes = sizeClasses[size]

  return (
    <div
      className={`
        ${sizes.card} 
        relative rounded-lg bg-white dark:bg-gray-100 shadow-lg border-2
        ${eliminated ? 'border-gray-300 dark:border-gray-600 opacity-40' : 'border-gray-200 dark:border-gray-300'}
        ${isAce && !eliminated ? 'ring-2 ring-blue-500 shadow-blue-500/30' : ''}
        font-bold transition-all
        ${!eliminated ? 'hover:scale-105 hover:shadow-xl' : ''}
      `}
    >
      {/* TOP-LEFT: Rank and small suit */}
      <div
        className={`absolute top-0.5 left-0.5 flex flex-col items-center leading-none ${suitData.color} ${eliminated ? 'opacity-50' : ''}`}
      >
        <span className={sizes.cornerRank}>{value}</span>
        <span className={sizes.cornerSuit}>{suit}</span>
      </div>

      {/* CENTER: Large suit */}
      <div className="flex-1 flex items-center justify-center">
        <span className={`${sizes.centerSuit} ${suitData.color} ${eliminated ? 'opacity-50' : ''}`}>
          {suit}
        </span>
      </div>

      {/* BOTTOM-RIGHT: Inverted rank and small suit */}
      <div
        className={`absolute bottom-0.5 right-0.5 flex flex-col items-center leading-none rotate-180 ${suitData.color} ${eliminated ? 'opacity-50' : ''}`}
      >
        <span className={sizes.cornerRank}>{value}</span>
        <span className={sizes.cornerSuit}>{suit}</span>
      </div>

      {eliminated && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-full h-0.5 bg-red-500 rotate-45 transform origin-center" />
        </div>
      )}
    </div>
  )
}

/**
 * FloatingCard - animated background card
 */
export const FloatingCard = ({ value, suit, className, style }) => (
  <div className={`absolute ${className}`} style={style}>
    <div className="w-16 h-[5.5rem] rounded-lg bg-white/20 dark:bg-white/10 backdrop-blur-sm border border-white/30 relative font-bold text-white/60 shadow-lg">
      <div className="absolute top-0.5 left-0.5 flex flex-col items-center leading-none">
        <span className="text-xs">{value}</span>
        <span className="text-[10px]">{suit}</span>
      </div>
      <div className="flex-1 flex items-center justify-center h-full">
        <span className="text-2xl">{suit}</span>
      </div>
      <div className="absolute bottom-0.5 right-0.5 flex flex-col items-center leading-none rotate-180">
        <span className="text-xs">{value}</span>
        <span className="text-[10px]">{suit}</span>
      </div>
    </div>
  </div>
)

/**
 * Hero section - Full-screen gradient with animated cards, featured Ace, live badge
 */
export function ChaseStyle1HeroSection({
  mainDraw,
  partner,
  onLoginClick,
  editMode,
  handleTranslationUpdate,
  showPartnerSelector = true,
  sections = [],
  isAdmin,
  updateSection,
}) {
  const { c } = useContent()
  const { t: tr } = useTranslations()
  const purchaseNavigate = usePurchaseNavigate(onLoginClick)
  const { countdownStyle } = useCountdownStyle()
  const { branding } = useCharityTheme()
  const { siteDetails } = useSiteConfig() || {}
  const showPartner = showPartnerSelector
  const { timeLeft, shouldAnimate } = useCountdown(mainDraw, { enableAnimation: true })
  const jackpotAmount = Math.floor(mainDraw?.current_jackpot ?? 0)
  const weeklyPrize = Math.floor(mainDraw?.weekly_prize ?? mainDraw?.secondary_prize ?? 0)
  const ticketsSold = mainDraw?.total_tickets_sold
  const cardsRemaining = mainDraw?.cards_remaining ?? 52
  const { language } = useLanguage()
  const ctaTarget = resolveCtaTargetCard(sections, siteDetails)
  const ctaTargetName = formatTargetCardName(ctaTarget.rank, ctaTarget.suit, language)
  const targetCardTextClass = ctaTarget.suit === '♥' || ctaTarget.suit === '♦' ? 'text-red-600' : 'text-gray-900'
  const targetCopy = (key, fallback) => formatCtaTargetCardText(c(key, fallback), ctaTarget, fallback, language)
  const sectionType = 'hero_section'
  const sectionConfig = sections.find(s => s.section_type === sectionType)?.config || {}
  const heroTitleLine1 = sectionConfig.heroTitleLine1 || c('chaseTheAce.heroTitleLine1', 'CHASE')
  const heroTitleLine2 = sectionConfig.heroTitleLine2 || c('chaseTheAce.heroTitleLine2', 'THE ACE')

  const showFloatingCards = sectionConfig.showFloatingCards ?? true
  const logoBackgroundColor = sectionConfig.logoBackgroundColor ?? '#ffffff'

  const heroSect = sections.find(s => s.section_type === sectionType)
  const handleHeroConfigChange = (patch) => {
    if (!heroSect || !updateSection) return
    updateSection(heroSect.id, { config: { ...heroSect.config, ...patch } })
  }

  const drawDateLabel = mainDraw?.draw_date
    ? new Date(mainDraw.draw_date).toLocaleDateString(undefined, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : ''

  const brandLogo = siteDetails?.siteLogo || charityConfig.logoUrl
  const brandName = siteDetails?.siteName || charityConfig.name
  const defaultPlatformLogo = 'https://cms.tlffs.ca/uploads/header_Logo_4f095fb08b.svg'
  const isDefaultLogo = branding?.logoUrl === defaultPlatformLogo
  const logoUrl = isDefaultLogo ? brandLogo : (branding?.logoUrl || brandLogo)
  const logoAlt = isDefaultLogo ? brandName : (branding?.charityName || brandName || 'Charity Partner')

  return (
    <section className="relative min-h-[80vh] lg:min-h-[70vh] pb-16 flex items-center overflow-hidden bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900">
      {isAdmin && (
        <div className="absolute top-16 right-4 z-50 rounded-xl bg-black/60 backdrop-blur-md border border-white/20 p-3 flex flex-col gap-2.5 min-w-[160px]">
          <p className="text-[10px] font-semibold text-white/60 uppercase tracking-wider">{tr('admin.heroSettings.title', 'Hero Settings')}</p>
          <div className="flex items-center justify-between gap-4">
            <span className="text-xs text-white">{tr('admin.heroSettings.floatingCards', 'Floating cards')}</span>
            <button
              onClick={() => handleHeroConfigChange({ showFloatingCards: !showFloatingCards })}
              className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none ${showFloatingCards ? 'bg-indigo-500' : 'bg-gray-500/70'}`}
              role="switch"
              aria-checked={showFloatingCards}
              aria-label={tr('admin.heroSettings.floatingCards', 'Floating cards')}
            >
              <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform ${showFloatingCards ? 'translate-x-4' : 'translate-x-0'}`} />
            </button>
          </div>
          <div>
            <span className="text-xs text-white block mb-1.5">{tr('admin.heroSettings.logoBackground', 'Logo background')}</span>
            <div className="flex gap-1.5 flex-wrap">
              {[
                { label: tr('admin.heroSettings.white', 'White'), value: '#ffffff' },
                { label: tr('admin.heroSettings.clear', 'Clear'), value: 'transparent' },
                { label: tr('admin.heroSettings.black', 'Black'), value: '#000000' },
                { label: tr('admin.heroSettings.dark', 'Dark'), value: '#1a1a1a' },
              ].map(preset => (
                <button
                  key={preset.value}
                  title={preset.label}
                  aria-label={`${tr('admin.heroSettings.logoBackground', 'Logo background')} ${preset.label}`}
                  onClick={() => handleHeroConfigChange({ logoBackgroundColor: preset.value })}
                  className={`w-5 h-5 rounded-full border-2 transition-all ${logoBackgroundColor === preset.value ? 'border-indigo-400 scale-110' : 'border-white/40'}`}
                  style={{
                    backgroundColor: preset.value === 'transparent' ? undefined : preset.value,
                    backgroundImage: preset.value === 'transparent' ? 'repeating-conic-gradient(#ccc 0% 25%, white 0% 50%)' : undefined,
                    backgroundSize: '4px 4px',
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Glowing orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse" />
        <div
          className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-500/15 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: '1s' }}
        />
        <div
          className="absolute top-1/2 right-1/3 w-64 h-64 bg-indigo-400/10 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: '2s' }}
        />

        {/* Floating cards background */}
        {showFloatingCards && (
          <>
            <FloatingCard
              value="A"
              suit="♠"
              className="top-20 left-[10%] opacity-30 animate-bounce"
              style={{ animationDuration: '3s' }}
            />
            <FloatingCard
              value="K"
              suit="♥"
              className="top-32 right-[15%] opacity-25 animate-bounce"
              style={{ animationDuration: '4s', animationDelay: '0.5s' }}
            />
            <FloatingCard
              value="Q"
              suit="♦"
              className="bottom-32 left-[20%] opacity-30 animate-bounce"
              style={{ animationDuration: '3.5s', animationDelay: '1s' }}
            />
            <FloatingCard
              value="J"
              suit="♣"
              className="bottom-20 right-[25%] opacity-25 animate-bounce"
              style={{ animationDuration: '4.5s', animationDelay: '1.5s' }}
            />
          </>
        )}
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-6">
        <HeroSiteLogo tone="dark" />
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Column - Main Content */}
          <div className="text-center">
            {/* Charity Logo */}
            {logoUrl && (
              <div className="mb-6 lg:mb-4 flex justify-center">
                <div className="relative group">
                  <div className="absolute -inset-3 bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400 rounded-2xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity" />
                  <div
                    className="relative rounded-2xl px-6 py-4 shadow-2xl"
                    style={{ backgroundColor: logoBackgroundColor === 'transparent' ? 'transparent' : logoBackgroundColor }}
                  >
                    <img
                      src={logoUrl}
                      alt={logoAlt}
                      className="h-16 sm:h-20 lg:h-24 w-auto object-contain max-w-[280px]"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Live Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 backdrop-blur-sm border border-emerald-500/30 text-emerald-400 rounded-full text-sm font-bold mb-6">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
              </span>
              Week {mainDraw?.week_number ?? mainDraw?.current_week_number ?? ''} —{' '}
              <InlineTranslationEdit
                translationKey="chaseTheAce.liveNow"
                value={c('chaseTheAce.liveNow')}
                fallback="LIVE NOW"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </div>

            {/* Title */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-4 leading-tight">
              <InlineTranslationEdit
                translationKey="chaseTheAce.heroTitleLine1"
                value={heroTitleLine1}
                fallback="CHASE"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-400">
                <InlineTranslationEdit
                  translationKey="chaseTheAce.heroTitleLine2"
                  value={heroTitleLine2}
                  fallback="THE ACE"
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              </span>
            </h1>

            {/* Tagline */}
            <p className="text-xl md:text-2xl text-blue-200/80 mb-6 lg:mb-4 max-w-xl mx-auto">
              <InlineTranslationEdit
                translationKey="chaseTheAce.subtitle"
                value={targetCopy('chaseTheAce.subtitle', `Draw a card. Find the ${ctaTargetName} and win the progressive jackpot!`)}
                fallback={`Draw a card. Find the ${ctaTargetName} and win the progressive jackpot!`}
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
              />
            </p>

            {/* Prize Display - Mobile */}
            <div className="lg:hidden mb-8">
              {showPartner && (
                <div className="mb-6">
                  <HeroPartnerGlassSelect
                    className="mx-auto max-w-sm"
                    label={c('heroPartners.label', 'Partner')}
                    placeholder={c('heroPartners.choosePartner', 'Choose a partner')}
                    autoAdvance={sectionConfig.partnerAutoAdvance === true}
                    onRequestPurchase={() => purchaseNavigate('/chase-the-ace-purchase')}
                  />
                </div>
              )}
              <div className="bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-600 rounded-3xl p-6 shadow-2xl shadow-blue-500/30">
                <p className="text-blue-100 font-bold uppercase text-sm tracking-wider mb-2">
                  <InlineTranslationEdit
                    translationKey="chaseTheAce.progressiveJackpot"
                    value={c('chaseTheAce.progressiveJackpot')}
                    fallback="Progressive Jackpot"
                    onUpdate={handleTranslationUpdate}
                    editMode={editMode}
                  />
                </p>
                <p className="text-5xl md:text-6xl font-black text-white">${jackpotAmount.toLocaleString()}</p>
              </div>
            </div>

            {/* Draw Date pill — above the CTA */}
            <div className="flex justify-center mb-4">
              <DrawDateBadge draw={mainDraw} />
            </div>

            {/* CTA Button */}
            <button
              onClick={() => purchaseNavigate('/chase-the-ace-purchase')}
              className="group relative w-full sm:w-auto px-10 py-5 bg-gradient-to-r from-blue-500 via-cyan-500 to-blue-500 text-white text-xl font-black rounded-2xl overflow-hidden transition-all hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/40"
            >
              <span className="relative z-10 flex items-center justify-center gap-3">
                <Ticket className="w-6 h-6" />
                <InlineTranslationEdit
                  translationKey="chaseTheAce.buyTickets"
                  value={c('chaseTheAce.buyTickets')}
                  fallback="Get Your Tickets"
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
            </button>

            {/* Quick Stats */}
            <div className="mt-6 flex flex-wrap justify-center gap-6 text-sm">
              {ticketsSold != null && (
                <div className="flex items-center gap-2 text-blue-200">
                  <Users className="w-4 h-4" />
                  <span>
                    <strong className="text-white">{ticketsSold.toLocaleString()}</strong>{' '}
                    <InlineTranslationEdit
                      translationKey="chaseTheAce.ticketsSoldLabel"
                      value={c('chaseTheAce.ticketsSoldLabel')}
                      fallback="tickets sold"
                      onUpdate={handleTranslationUpdate}
                      editMode={editMode}
                    />
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2 text-blue-200">
                <Trophy className="w-4 h-4" />
                <span>
                  <InlineTranslationEdit
                    translationKey="chaseTheAce.weeklyLabel"
                    value={c('chaseTheAce.weeklyLabel')}
                    fallback="Weekly:"
                    onUpdate={handleTranslationUpdate}
                    editMode={editMode}
                  />{' '}
                  <strong className="text-white">${weeklyPrize.toLocaleString()}</strong>
                </span>
              </div>
            </div>
          </div>

          {/* Right Column - Jackpot Card & Countdown */}
          <div className="hidden lg:flex flex-col items-center gap-4">
            {showPartner && (
              <HeroPartnerGlassSelect
                className="w-full max-w-md"
                label={c('heroPartners.label', 'Partner')}
                placeholder={c('heroPartners.choosePartner', 'Choose a partner')}
                autoAdvance={sectionConfig.partnerAutoAdvance === true}
                onRequestPurchase={() => purchaseNavigate('/chase-the-ace-purchase')}
              />
            )}
            {/* Jackpot Card */}
            <div className="relative w-full max-w-md">
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 rounded-3xl blur-xl opacity-50" />
              <div className="relative bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 rounded-3xl p-6 shadow-2xl border border-white/20">
                <p className="text-blue-200 font-bold uppercase text-sm tracking-wider mb-2 text-center">
                  <InlineTranslationEdit
                    translationKey="chaseTheAce.progressiveJackpot"
                    value={c('chaseTheAce.progressiveJackpot')}
                    fallback="Progressive Jackpot"
                    onUpdate={handleTranslationUpdate}
                    editMode={editMode}
                  />
                </p>
                <p className="text-6xl xl:text-7xl font-black text-white text-center mb-3">
                  ${jackpotAmount.toLocaleString()}
                </p>

                {/* Featured target card */}
                <div className="flex justify-center mb-3">
                  <div className="w-16 h-24 rounded-xl bg-white shadow-2xl border-4 border-blue-300 relative transform hover:scale-110 transition-all cursor-pointer">
                    <div className={`absolute top-1 left-1.5 flex flex-col items-center leading-none ${targetCardTextClass}`}>
                      <span className="text-sm font-black">{ctaTarget.rank}</span>
                      <span className="text-xs">{ctaTarget.suit}</span>
                    </div>
                    <div className="flex-1 flex items-center justify-center h-full">
                      <span className={`text-4xl ${targetCardTextClass}`}>{ctaTarget.suit}</span>
                    </div>
                    <div className={`absolute bottom-1 right-1.5 flex flex-col items-center leading-none rotate-180 ${targetCardTextClass}`}>
                      <span className="text-sm font-black">{ctaTarget.rank}</span>
                      <span className="text-xs">{ctaTarget.suit}</span>
                    </div>
                  </div>
                </div>

                <p className="text-center text-blue-100 text-sm">
                  <InlineTranslationEdit
                    translationKey="chaseTheAce.findTheAce"
                    value={targetCopy('chaseTheAce.findTheAce', `Find the ${ctaTargetName} to win!`)}
                    fallback={`Find the ${ctaTargetName} to win!`}
                    onUpdate={handleTranslationUpdate}
                    editMode={editMode}
                  />
                </p>
              </div>
            </div>

            {/* Countdown Timer */}
            <div className="bg-white/15 backdrop-blur-sm rounded-2xl px-5 py-4 border border-white/30 w-full max-w-md">
              <div className="text-center mb-3 text-xs font-semibold uppercase tracking-wider text-blue-200">
                <InlineTranslationEdit
                  translationKey="chaseTheAce.drawIn"
                  value={c('chaseTheAce.drawIn')}
                  fallback="Next Draw In"
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              </div>
              <div className="flex gap-3 justify-center">
                {[
                  { val: timeLeft.days, label: 'Days' },
                  { val: timeLeft.hours, label: 'Hrs' },
                  { val: timeLeft.minutes, label: 'Min' },
                  { val: timeLeft.seconds, label: 'Sec' },
                ].map((item, i) => (
                  <div key={i} className="flex flex-col items-center">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/25 backdrop-blur-sm rounded-xl flex items-center justify-center text-xl sm:text-2xl font-black text-white shadow-lg border border-white/30">
                      {String(item.val).padStart(2, '0')}
                    </div>
                    <span className="text-[10px] text-blue-200 mt-1 font-medium">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Odds Display */}
            <div className="text-center">
              <p className="text-blue-300 text-sm">
                {(tr('chaseTheAce.cardsRemaining') || '{{count}} cards remaining').replace('{{count}}', cardsRemaining)}
                {' • '}
                {(tr('chaseTheAce.cardsOdds') || '1 in {{count}} odds').replace('{{count}}', cardsRemaining)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

/**
 * Cards Remaining section - Full deck display by suit on felt-green background
 */
export function ChaseStyle1CardsRemainingSection({
  mainDraw,
  eliminatedCards = [],
  deck = [],
  editMode,
  isAdmin,
  handleTranslationUpdate,
  sections = [],
}) {
  const { c } = useContent()
  const { t: tr } = useTranslations()
  const { siteDetails } = useSiteConfig() || {}
  const cardsRemaining = mainDraw?.cards_remaining ?? (52 - eliminatedCards.length)
  const { language } = useLanguage()
  const ctaTarget = resolveCtaTargetCard(sections, siteDetails)
  const ctaTargetName = formatTargetCardName(ctaTarget.rank, ctaTarget.suit, language)
  const targetCopy = (key, fallback) => formatCtaTargetCardText(c(key, fallback), ctaTarget, fallback, language)
  const jackpotAmount = Math.floor(mainDraw?.current_jackpot ?? 0)
  const inlineEditMode = editMode || isAdmin
  const jackpotText = `$${jackpotAmount.toLocaleString()}`
  const oddsText = `1 in ${cardsRemaining}`
  const findTargetTemplate = c('chaseTheAce.findTargetCardCallout', 'Find the {{targetCard}} to win!')
  const jackpotTemplate = c('chaseTheAce.targetCardJackpotLine', 'Win the {{jackpotAmount}} Jackpot!')
  const oddsTemplate = c('chaseTheAce.targetCardOddsLine', 'Current odds: {{odds}}')
  const findTargetDisplay = formatCtaTargetCardText(findTargetTemplate, ctaTarget, 'Find the {{targetCard}} to win!', language)
  const jackpotDisplay = formatDynamicText(jackpotTemplate, { jackpotAmount: jackpotText, odds: oddsText, targetCard: ctaTargetName })
  const oddsDisplay = formatDynamicText(oddsTemplate, { jackpotAmount: jackpotText, odds: oddsText, targetCard: ctaTargetName })

  return (
    <section className="py-12 bg-gradient-to-b from-gray-100 to-gray-50 dark:from-gray-900 dark:to-gray-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-br from-emerald-800 via-emerald-900 to-green-950 rounded-3xl p-6 md:p-8 shadow-2xl border border-emerald-700/50 relative overflow-hidden">
          {/* Felt texture overlay */}
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage:
                'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)',
              backgroundSize: '16px 16px',
            }}
          />

          <div className="relative z-10">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-white flex items-center gap-3">
                <span className="text-3xl">🃏</span>
                <InlineTranslationEdit
                  translationKey="chaseTheAce.theDeck"
                  value={c('chaseTheAce.theDeck')}
                  fallback="The Deck"
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              </h2>
              <div className="flex items-center gap-2">
                <span className="px-3 py-1.5 bg-emerald-500/20 backdrop-blur-sm text-emerald-200 rounded-full text-sm font-bold border border-emerald-400/30">
                  {cardsRemaining} {tr('chaseTheAce.inPlay') || 'In Play'}
                </span>
                <span className="px-3 py-1.5 bg-red-500/20 backdrop-blur-sm text-red-300 rounded-full text-sm font-bold border border-red-400/30">
                  {eliminatedCards.length} {tr('chaseTheAce.cardsOut') || 'Out'}
                </span>
              </div>
            </div>

            {/* Card Grid — picker-page style (52 numbered cards, 13 cols × 4 rows) */}
            {deck && deck.length > 0 ? (
              <div className="bg-gradient-to-br from-gray-400 to-gray-500 dark:from-gray-600 dark:to-gray-700 rounded-xl p-3 sm:p-4 overflow-x-auto">
                <div className="grid grid-cols-13 gap-1.5 sm:gap-2 min-w-[480px]">
                  {[...deck]
                    .sort((a, b) => Number(a.cardNumber ?? a.num ?? 0) - Number(b.cardNumber ?? b.num ?? 0))
                    .map((card) => {
                      const isDrawn = !!card.drawn
                      const suit = String(card.suit || '').toLowerCase()
                      const suitSym = { hearts: '♥', diamonds: '♦', clubs: '♣', spades: '♠' }[suit] || ''
                      const isRed = suit === 'hearts' || suit === 'diamonds'
                      const rankColor = isRed ? 'text-red-600' : 'text-gray-900'
                      const num = card.cardNumber ?? card.num ?? card.number
                      const rank = typeof card.number === 'string' && isNaN(card.number)
                        ? card.number[0]
                        : (card.number ?? card.rank ?? num)
                      return (
                        <div
                          key={card.id || num}
                          className="aspect-[2/3] rounded-md relative shadow-md border-2 border-white overflow-hidden"
                        >
                          {isDrawn ? (
                            <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #ffffff 0%, #eff2f8 100%)' }}>
                              <div className={`absolute top-0.5 left-1 ${rankColor}`}>
                                <span className="text-[10px] sm:text-xs font-bold leading-none">{rank}</span>
                              </div>
                              <div className={`absolute inset-0 flex items-center justify-center ${rankColor}`}>
                                <span className="text-base sm:text-xl font-bold">{suitSym}</span>
                              </div>
                              <div className={`absolute bottom-0.5 right-1 ${rankColor}`}>
                                <span className="text-[10px] sm:text-xs font-bold leading-none">{rank}</span>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div
                                className="w-full h-full"
                                style={{
                                  backgroundColor: '#1e40af',
                                  backgroundImage: `linear-gradient(45deg, rgba(30, 58, 138, 0.7) 25%, transparent 25%, transparent 50%, rgba(30, 58, 138, 0.7) 50%, rgba(30, 58, 138, 0.7) 75%, transparent 75%), linear-gradient(45deg, rgba(30, 58, 138, 0.7) 25%, transparent 25%, transparent 50%, rgba(30, 58, 138, 0.7) 50%, rgba(30, 58, 138, 0.7) 75%, transparent 75%)`,
                                  backgroundSize: '14px 14px',
                                  backgroundPosition: '0 0, 7px 7px',
                                  border: '2px solid rgba(255, 255, 255, 0.3)',
                                  margin: '2px',
                                }}
                              />
                              <div className="absolute inset-0 flex items-center justify-center">
                                <span className="text-white font-bold text-sm sm:text-lg drop-shadow-lg">{num}</span>
                              </div>
                            </>
                          )}
                        </div>
                      )
                    })}
                </div>
              </div>
            ) : (
            /* Fallback: suit-organized grid when deck data not yet available */
            <div className="space-y-2 sm:space-y-3">
              {suits.map(suit => (
                <div key={suit.symbol} className="flex items-center gap-1 sm:gap-2">
                  {/* Suit label */}
                  <div
                    className={`w-6 sm:w-8 flex items-center justify-center text-lg sm:text-2xl flex-shrink-0 ${
                      suit.color === 'text-red-500' ? 'text-red-400' : 'text-white'
                    }`}
                  >
                    {suit.symbol}
                  </div>

                  {/* Cards row */}
                  <div className="flex-1 flex gap-0.5 sm:gap-1">
                    {cardValues.map(value => {
                      const cardStr = `${value}${suit.symbol}`
                      const isEliminated = eliminatedCards.includes(cardStr)
                      const isAce = value === ctaTarget.rank && suit.symbol === ctaTarget.suit
                      const isRed = suit.color === 'text-red-500'

                      const rankColor = isAce ? 'text-amber-900' : isRed ? 'text-red-600' : 'text-gray-900'
                      return (
                        <div
                          key={cardStr}
                          className={`
                            flex-1 min-w-0 aspect-[2.5/3.5] rounded-md transition-all duration-200 cursor-pointer relative
                            ${
                              isEliminated
                                ? isAce
                                  ? 'bg-gradient-to-br from-amber-200 via-yellow-100 to-amber-300 border-2 border-amber-400 ring-2 ring-amber-400/60 shadow-lg shadow-amber-500/50 hover:scale-110 hover:-rotate-3 hover:z-10'
                                  : 'bg-white border border-gray-300 shadow-md hover:shadow-xl hover:-translate-y-1 hover:z-10'
                                : 'border border-blue-700/70 shadow-inner shadow-black/30'
                            }
                          `}
                          style={
                            isEliminated
                              ? undefined
                              : {
                                  backgroundColor: '#1e1b4b',
                                  backgroundImage:
                                    'repeating-linear-gradient(45deg, rgba(255,255,255,0.10) 0 5px, transparent 5px 10px), repeating-linear-gradient(-45deg, rgba(255,255,255,0.07) 0 5px, transparent 5px 10px), radial-gradient(circle at 50% 50%, rgba(99,102,241,0.35) 0%, transparent 70%)',
                                }
                          }
                        >
                          {isEliminated ? (
                            <>
                              <div
                                className={`absolute top-0.5 left-0.5 flex flex-col items-center leading-none font-black text-[6px] sm:text-[9px] md:text-[11px] ${rankColor}`}
                              >
                                <span>{value}</span>
                                <span className="text-[5px] sm:text-[8px] md:text-[10px]">{suit.symbol}</span>
                              </div>
                              <div
                                className={`absolute inset-0 flex items-center justify-center text-[10px] sm:text-base md:text-lg font-bold ${rankColor}`}
                              >
                                {suit.symbol}
                              </div>
                              <div
                                className={`absolute bottom-0.5 right-0.5 flex flex-col items-center leading-none rotate-180 font-black text-[6px] sm:text-[9px] md:text-[11px] ${rankColor}`}
                              >
                                <span>{value}</span>
                                <span className="text-[5px] sm:text-[8px] md:text-[10px]">{suit.symbol}</span>
                              </div>
                              <div className="absolute inset-0 bg-gradient-to-br from-white/50 via-transparent to-transparent rounded-md pointer-events-none" />
                              {isAce && (
                                <div className="absolute inset-0 bg-gradient-to-t from-amber-400/40 to-transparent animate-pulse rounded-md pointer-events-none" />
                              )}
                            </>
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center text-white/30 text-[10px] sm:text-sm md:text-base pointer-events-none" aria-hidden="true">
                              ♠
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
            )}

            {/* Legend */}
            <div className="mt-6 flex flex-wrap justify-center gap-4 text-xs">
              <div className="flex items-center gap-2 text-emerald-200">
                <div className="w-5 h-7 rounded-sm bg-white border border-gray-300 shadow" />
                <span>
                  <InlineTranslationEdit
                    translationKey="chaseTheAce.inPlayLabel"
                    value={c('chaseTheAce.inPlayLabel')}
                    fallback="In Play"
                    onUpdate={handleTranslationUpdate}
                    editMode={editMode}
                  />
                </span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <div className="w-5 h-7 rounded-sm bg-gray-700/60 border border-gray-600/50 flex items-center justify-center text-[8px]">
                  ✕
                </div>
                <span>
                  <InlineTranslationEdit
                    translationKey="chaseTheAce.eliminatedLabel"
                    value={c('chaseTheAce.eliminatedLabel')}
                    fallback="Eliminated"
                    onUpdate={handleTranslationUpdate}
                    editMode={editMode}
                  />
                </span>
              </div>
              <div className="flex items-center gap-2 text-amber-300">
                <div className="w-5 h-7 rounded-sm bg-gradient-to-br from-amber-300 to-amber-400 border border-amber-400 shadow-lg shadow-amber-500/30" />
                <span>
                  <InlineTranslationEdit
                    translationKey="chaseTheAce.aceJackpotLabel"
                    value={targetCopy('chaseTheAce.aceJackpotLabel', `${ctaTargetName} (Jackpot!)`)}
                    fallback={`${ctaTargetName} (Jackpot!)`}
                    onUpdate={handleTranslationUpdate}
                    editMode={editMode}
                  />
                </span>
              </div>
            </div>

            {/* Featured target card */}
            <div className="mt-8 flex items-center justify-center">
              <div className="bg-black/20 backdrop-blur-sm rounded-2xl p-5 border border-white/10 flex items-center gap-5">
                <PlayingCard value={ctaTarget.rank} suit={ctaTarget.suit} size="lg" isAce />
                <div className="text-white">
                  <p className="text-lg font-bold">
                    <InlineTranslationEdit
                      translationKey="chaseTheAce.findTargetCardCallout"
                      value={findTargetTemplate}
                      displayValue={findTargetDisplay}
                      fallback="Find the {{targetCard}} to win!"
                      onUpdate={handleTranslationUpdate}
                      editMode={inlineEditMode}
                    />
                  </p>
                  <p className="text-emerald-200 text-sm">
                    <InlineTranslationEdit
                      translationKey="chaseTheAce.targetCardJackpotLine"
                      value={jackpotTemplate}
                      displayValue={jackpotDisplay}
                      fallback="Win the {{jackpotAmount}} Jackpot!"
                      onUpdate={handleTranslationUpdate}
                      editMode={inlineEditMode}
                    />
                  </p>
                  <p className="text-emerald-300/60 text-xs mt-1">
                    <InlineTranslationEdit
                      translationKey="chaseTheAce.targetCardOddsLine"
                      value={oddsTemplate}
                      displayValue={oddsDisplay}
                      fallback="Current odds: {{odds}}"
                      onUpdate={handleTranslationUpdate}
                      editMode={inlineEditMode}
                    />
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

/**
 * How It Works section - 4 steps + Prize Structure + Key Rules
 */
export function ChaseStyle1HowItWorksSection({ editMode, handleTranslationUpdate, sections = [] }) {
  const { c } = useContent()
  const { t: tr } = useTranslations()
  const { language } = useLanguage()
  const { siteDetails } = useSiteConfig() || {}
  const isManualEnvelopeMode = resolveCtaManualEnvelopeMode(siteDetails, sections)
  const ctaTarget = resolveCtaTargetCard(sections, siteDetails)
  const ctaTargetName = formatTargetCardName(ctaTarget.rank, ctaTarget.suit, language)
  const targetCopy = (key, fallback) => formatCtaTargetCardText(c(key, tr(key) || fallback), ctaTarget, fallback, language)

  const steps = [
    {
      icon: '🎟️',
      step: '1',
      titleKey: 'chaseTheAce.step1Title',
      descKey: 'chaseTheAce.step1Desc',
      title: isManualEnvelopeMode ? 'Buy Your Tickets' : 'Buy Tickets & Choose Envelope',
      desc: isManualEnvelopeMode
        ? 'Purchase tickets for the weekly draw. More tickets = more chances!'
        : 'Purchase tickets and select your envelope for the draw. More tickets = more chances!',
    },
    {
      icon: '✉️',
      step: '2',
      titleKey: 'chaseTheAce.step2Title',
      descKey: 'chaseTheAce.step2Desc',
      title: 'Weekly Draw',
      desc: isManualEnvelopeMode
        ? "One ticket is drawn each week. If it's yours, you select an envelope!"
        : 'One ticket is drawn each week.',
    },
    {
      icon: '✉️',
      step: '3',
      titleKey: 'chaseTheAce.step3Title',
      descKey: 'chaseTheAce.step3Desc',
      title: isManualEnvelopeMode ? 'Pick an Envelope' : 'Reveal the Envelope',
      desc: isManualEnvelopeMode
        ? 'Choose an envelope containing a card from the deck to reveal live.'
        : "The winner's pre-selected envelope is opened live to reveal the card inside.",
    },
    {
      icon: '🏆',
      step: '4',
      titleKey: 'chaseTheAce.step4Title',
      descKey: 'chaseTheAce.step4Desc',
      title: 'Win Prizes!',
      desc: `${ctaTargetName} = Jackpot! Any other card = Weekly Prize + card eliminated.`,
    },
  ]

  const rules = [
    {
      icon: Clock,
      titleKey: 'chaseTheAce.rule1Title',
      descKey: 'chaseTheAce.rule1Desc',
      title: 'Weekly Draws',
      desc: 'Every week, one lucky ticket holder reveals a card from the deck.',
    },
    {
      icon: TrendingUp,
      titleKey: 'chaseTheAce.rule2Title',
      descKey: 'chaseTheAce.rule2Desc',
      title: 'Growing Jackpot',
      desc: `Jackpot grows each week until the ${ctaTargetName} is found!`,
    },
    {
      icon: Users,
      titleKey: 'chaseTheAce.rule3Title',
      descKey: 'chaseTheAce.rule3Desc',
      title: 'Everyone Wins',
      desc: `Win the weekly prize if you don't find the ${ctaTargetName}. Better odds each week!`,
    },
  ]

  return (
    <section className="py-12 bg-white dark:bg-gray-800 border-y border-gray-200 dark:border-gray-700">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3">
            <InlineTranslationEdit
              translationKey="chaseTheAce.howItWorksTitle"
              value={targetCopy('chaseTheAce.howItWorksTitle', `How to Play Chase the ${ctaTargetName}`)}
              fallback={`How to Play Chase the ${ctaTargetName}`}
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
            />
          </h2>
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            <InlineTranslationEdit
              translationKey="chaseTheAce.howItWorksDesc"
              value={targetCopy('chaseTheAce.howItWorksDesc', `A thrilling card game where the ${ctaTargetName} holds the jackpot — be the one to find it!`)}
              fallback={`A thrilling card game where the ${ctaTargetName} holds the jackpot — be the one to find it!`}
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
            />
          </p>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-4 gap-6 mb-12">
          {steps.map((item, i) => (
            <div key={i} className="relative text-center">
              {i < 3 && (
                <div className="hidden md:block absolute top-12 left-1/2 w-full h-0.5 bg-gradient-to-r from-blue-200 to-blue-400" />
              )}
              <div className="relative bg-white dark:bg-gray-700 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-600 hover:shadow-xl transition-shadow">
                <div className="text-4xl mb-3">{item.icon}</div>
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-bold flex items-center justify-center mx-auto mb-3">
                  {item.step}
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                  <InlineTranslationEdit
                    translationKey={item.titleKey}
                    value={targetCopy(item.titleKey, item.title)}
                    fallback={item.title}
                    onUpdate={handleTranslationUpdate}
                    editMode={editMode}
                  />
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  <InlineTranslationEdit
                    translationKey={item.descKey}
                    value={targetCopy(item.descKey, item.desc)}
                    fallback={item.desc}
                    onUpdate={handleTranslationUpdate}
                    editMode={editMode}
                  />
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Key Rules */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl p-8 border border-blue-200 dark:border-blue-800">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 text-center">
            <InlineTranslationEdit
              translationKey="chaseTheAce.keyRulesTitle"
              value={c('chaseTheAce.keyRulesTitle')}
              fallback="Key Rules"
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
            />
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            {rules.map((rule, i) => {
              const Icon = rule.icon
              return (
                <div key={i} className="flex gap-3">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-lg bg-blue-500 dark:bg-blue-600 text-white flex items-center justify-center">
                      <Icon className="w-5 h-5" />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                      <InlineTranslationEdit
                        translationKey={rule.titleKey}
                        value={c(rule.titleKey, tr(rule.titleKey))}
                        fallback={rule.title}
                        onUpdate={handleTranslationUpdate}
                        editMode={editMode}
                      />
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <InlineTranslationEdit
                        translationKey={rule.descKey}
                        value={targetCopy(rule.descKey, rule.desc)}
                        fallback={rule.desc}
                        onUpdate={handleTranslationUpdate}
                        editMode={editMode}
                      />
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}

/**
 * Final CTA section - Dark gradient with featured Ace, remaining cards count, odds
 */
export function ChaseStyle1FinalCTASection({
  mainDraw,
  eliminatedCards = [],
  onLoginClick,
  editMode,
  handleTranslationUpdate,
  sections = [],
}) {
  const { c } = useContent()
  const { t: tr } = useTranslations()
  const { siteDetails } = useSiteConfig() || {}
  const purchaseNavigate = usePurchaseNavigate(onLoginClick)
  const jackpotAmount = Math.floor(mainDraw?.current_jackpot ?? 0)
  const cardsRemaining = mainDraw?.cards_remaining ?? (52 - eliminatedCards.length)
  const { language } = useLanguage()
  const ctaTarget = resolveCtaTargetCard(sections, siteDetails)
  const ctaTargetName = formatTargetCardName(ctaTarget.rank, ctaTarget.suit, language)
  const targetCopy = (key, fallback) => formatCtaTargetCardText(c(key, tr(key) || fallback), ctaTarget, fallback, language)

  return (
    <section className="py-16 bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-10 text-9xl">♠</div>
        <div className="absolute bottom-10 right-10 text-9xl">♠</div>
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="mb-8">
          <PlayingCard value={ctaTarget.rank} suit={ctaTarget.suit} size="xl" isAce />
        </div>

        <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
          <InlineTranslationEdit
            translationKey="chaseTheAce.finalCTATitle"
            value={targetCopy('chaseTheAce.finalCTATitle', `Will You Find the ${ctaTargetName}?`)}
            fallback={`Will You Find the ${ctaTargetName}?`}
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </h2>

        <p className="text-lg sm:text-xl text-blue-200 mb-6 sm:mb-8 px-2">
          <span className="font-bold text-white">{cardsRemaining}</span>{' '}
          <InlineTranslationEdit
            translationKey="chaseTheAce.cardsRemaining"
            value={c('chaseTheAce.cardsRemaining')}
            fallback="{{count}} cards remaining"
            displayValue={(c('chaseTheAce.cardsRemaining') || '{{count}} cards remaining').replace('{{count}}', cardsRemaining)}
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />{' '}
          •{' '}
          <InlineTranslationEdit
            translationKey="chaseTheAce.cardsOdds"
            value={c('chaseTheAce.cardsOdds')}
            fallback="1 in {{count}} odds"
            displayValue={(c('chaseTheAce.cardsOdds') || '1 in {{count}} odds').replace('{{count}}', cardsRemaining)}
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />{' '}
          •{' '}
          <span className="font-bold text-emerald-400">{formatCurrencyValue(jackpotAmount, { language })}</span>
        </p>

        <button
          onClick={() => purchaseNavigate('/chase-the-ace-purchase')}
          className="group inline-flex items-center justify-center gap-2 sm:gap-3 px-6 sm:px-12 py-3 sm:py-5 bg-gradient-to-r from-emerald-500 via-green-500 to-emerald-500 text-white text-base sm:text-xl font-black rounded-2xl shadow-2xl hover:scale-105 transition-all hover:shadow-emerald-500/40 w-full sm:w-auto"
        >
          <Ticket className="w-6 h-6" />
          <InlineTranslationEdit
            translationKey="chaseTheAce.getTicketsNow"
            value={c('chaseTheAce.getTicketsNow')}
            fallback="Get Your Tickets Now"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
        </button>
      </div>
    </section>
  )
}
